import { toRelativeUrl } from '@okta/okta-auth-js';
import { useOktaAuth } from '@okta/okta-react';
import { Button } from '@optools/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from '@optools/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@optools/components/ui/tooltip';
import { cn } from '@optools/components/ui/utils';
import { useOktaProfile } from '@optools/hooks/useOktaProfile';
import { CircleUserRound, LogIn, LogOut } from 'lucide-react';
import { useState } from 'react';

interface AccountMenuProps {
  sidebarOpen: boolean;
}

export function AccountMenu({ sidebarOpen }: AccountMenuProps) {
  const { authState, oktaAuth } = useOktaAuth();
  const isAuthenticated = !!authState?.isAuthenticated;
  const { profile } = useOktaProfile();

  const [open, setOpen] = useState(false);
  const handleOpenChange = (nextOpen: boolean) => {
    if (!sidebarOpen) setOpen(nextOpen);
  };

  const handleAuthClick = () => {
    if (isAuthenticated) {
      oktaAuth.signOut({ postLogoutRedirectUri: `${window.location.origin}/login` });
      return;
    }

    const originalUri = toRelativeUrl(window.location.href, window.location.origin);
    oktaAuth.setOriginalUri(originalUri);
    oktaAuth.signInWithRedirect();
  };

  return (
    <Tooltip open={open} onOpenChange={handleOpenChange}>
      <Popover>
        <PopoverTrigger asChild>
          <TooltipTrigger asChild>
            <Button
              aria-label="Account"
              className={cn(
                'justify-start gap-2 overflow-hidden pl-2 transition-all duration-300',
                sidebarOpen ? 'w-full' : 'w-10',
              )}
              size="icon-lg"
              variant="ghost">
              <CircleUserRound className="size-5 shrink-0" />
              <span
                className={cn(
                  'truncate transition-opacity duration-300',
                  sidebarOpen ? 'opacity-100 delay-100' : 'opacity-0',
                )}>
                {profile?.fullName || 'Account'}
              </span>
            </Button>
          </TooltipTrigger>
        </PopoverTrigger>

        <PopoverContent
          align="end"
          alignOffset={2}
          className="w-max gap-0 p-0"
          side="right"
          sideOffset={8}>
          {isAuthenticated && (
            <PopoverHeader className="flex flex-col gap-1 border-b p-3">
              <PopoverTitle>{profile.fullName}</PopoverTitle>
              <PopoverDescription>{profile.email}</PopoverDescription>
            </PopoverHeader>
          )}

          <Button
            className="w-full justify-start rounded-lg rounded-tl-none rounded-tr-none"
            size="sm"
            variant="ghost"
            onClick={handleAuthClick}>
            {isAuthenticated ? <LogOut /> : <LogIn />}
            <span>{isAuthenticated ? 'Logout' : 'Login'}</span>
          </Button>
        </PopoverContent>
      </Popover>

      <TooltipContent side="right" sideOffset={5}>
        Account
      </TooltipContent>
    </Tooltip>
  );
}
